import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { PhoneCall, Check, MessageSquare, CalendarClock, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtWhen = (iso) => iso ? new Date(iso).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";

export const FollowUpTasks = ({ onOpenAppointment, refreshKey }) => {
  const [tasks, setTasks] = useState([]);
  const [showDone, setShowDone] = useState(false);
  const navigate = useNavigate();
  const load = useCallback(() => axios.get(`${API}/tasks`, { params: { status: showDone ? "all" : "open" } }).then((r) => setTasks(r.data.tasks)).catch(() => {}), [showDone]);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load, refreshKey]);
  const setStatus = async (task, status) => {
    try {
      await axios.put(`${API}/tasks/${task.id}`, { status });
      toast.success(status === "done" ? "Follow-up marked done" : "Follow-up reopened");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not update follow-up"); }
  };
  const open = tasks.filter((t) => t.status === "open");
  if (tasks.length === 0 && !showDone) return null;
  return (
    <Card className="bg-white border-0 shadow-lg border-l-4 border-l-amber-400" data-testid="follow-up-tasks">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-base md:text-lg font-semibold text-slate-800 flex items-center gap-2"><PhoneCall className="w-4 h-4 text-amber-600" /> Office follow-ups {open.length > 0 && <Badge className="bg-amber-100 text-amber-800 border-amber-200" data-testid="follow-up-open-count">{open.length} open</Badge>}</h2>
          <button className="text-xs text-slate-500 hover:text-slate-800 flex items-center gap-1" onClick={() => setShowDone((v) => !v)} data-testid="follow-up-toggle-done">{showDone ? <><ChevronUp className="w-3 h-3" /> Hide completed</> : <><ChevronDown className="w-3 h-3" /> Show completed</>}</button>
        </div>
        {tasks.length === 0 && <p className="text-sm text-slate-400" data-testid="follow-up-empty">No follow-ups.</p>}
        <ul className="divide-y divide-slate-100">
          {tasks.map((t) => (
            <li key={t.id} className={`py-3 flex flex-col md:flex-row md:items-center gap-2 ${t.status === "done" ? "opacity-60" : ""}`} data-testid="follow-up-task">
              <div className="flex-1 min-w-0">
                <p className="font-medium text-slate-800 text-sm" data-testid="follow-up-title">{t.title}</p>
                <p className="text-xs text-slate-500">{t.details}</p>
                <p className="text-[11px] text-slate-400 mt-0.5">Requested {fmtWhen(t.created_at)}{t.status === "done" ? ` · done ${fmtWhen(t.done_at)} by ${t.done_by || ""}` : ""}</p>
              </div>
              <div className="flex gap-1 shrink-0">
                <Button size="sm" variant="outline" onClick={() => navigate(`/messages?phone=${t.phone}`)} title="Open text conversation" data-testid="follow-up-text-button"><MessageSquare className="w-4 h-4 mr-1" /> Text</Button>
                <Button size="sm" variant="outline" onClick={() => onOpenAppointment(t.appointment_id)} title="Open appointment" data-testid="follow-up-open-appointment"><CalendarClock className="w-4 h-4 mr-1" /> Appointment</Button>
                {t.status === "open"
                  ? <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setStatus(t, "done")} data-testid="follow-up-done-button"><Check className="w-4 h-4 mr-1" /> Done</Button>
                  : <Button size="sm" variant="ghost" onClick={() => setStatus(t, "open")} data-testid="follow-up-reopen-button">Reopen</Button>}
              </div>
            </li>
          ))}
        </ul>
      </CardContent>
    </Card>
  );
};
